import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  configureApiClient,
  type AuthSession,
  type LoginResponse,
  type Permissions,
} from '@/shared/api';
import { login as loginRequest, pingAuth, type LoginUser } from '@/entities/user';

const STORAGE_KEY = 'aria.admin.session';

interface AuthContextValue {
  session: AuthSession | null;
  user: LoginUser | null;
  permissions: Permissions | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  applySession: (result: LoginResponse) => void;
  updateUser: (user: LoginUser) => void;
  logout: () => void;
  hasPermission: (key: keyof Permissions) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function writeStoredSession(session: AuthSession | null) {
  if (!session) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function sessionFromLogin(result: LoginResponse): AuthSession {
  return {
    token: result.token,
    expiresAt: result.expires_at,
    user: result.user,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => readStoredSession());
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    setSession(null);
    writeStoredSession(null);
  }, []);

  const applySession = useCallback((result: LoginResponse) => {
    const next = sessionFromLogin(result);
    writeStoredSession(next);
    setSession(next);
  }, []);

  const updateUser = useCallback((user: LoginUser) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next = { ...prev, user };
      writeStoredSession(next);
      return next;
    });
  }, []);

  useEffect(() => {
    configureApiClient({
      getToken: () => readStoredSession()?.token ?? null,
      onUnauthorized: () => {
        writeStoredSession(null);
        setSession(null);
      },
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      const stored = readStoredSession();
      if (!stored) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      try {
        await pingAuth();
        if (!cancelled) {
          setSession(stored);
        }
      } catch {
        writeStoredSession(null);
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void validate();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await loginRequest(email, password);
      applySession(result);
    },
    [applySession],
  );

  const value = useMemo<AuthContextValue>(() => {
    const permissions = session?.user.permissions ?? null;
    return {
      session,
      user: session?.user ?? null,
      permissions,
      isLoading,
      isAuthenticated: Boolean(session),
      login,
      applySession,
      updateUser,
      logout,
      hasPermission: (key) => {
        if (!permissions) return false;
        if (!(key in permissions)) {
          if (
            key === 'can_view_chats' ||
            key === 'can_view_crm' ||
            key === 'can_use_playground' ||
            key === 'can_view_remarks'
          ) {
            return true;
          }
          if (key === 'can_manage_users') return session?.user.role_key === 'admin';
        }
        return Boolean(permissions[key]);
      },
    };
  }, [session, isLoading, login, applySession, updateUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
